import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";

import { login } from "../api-service/auth";
import { ROUTES } from "../common";
import messages from "../messages/ja.json";
import { AuthShell } from "../parts/auth-shell";
import { Icon, EyeIcon, EyeOffIcon } from "../parts/icon";
import { useAuth } from "../provider";

import "../styles/auth-flow.css";

interface LoginForm {
  username: string;
  password: string;
}

const schema = yup.object({
  username: yup.string().required(messages.auth.userInfoRequired),
  password: yup.string().required(messages.auth.passwordRequired),
});

// Login (backend /v1/auth/login). Routing is driven by challenge_name:
//   FACE_MFA              → /verify-mfa
//   FACE_REGISTRATION     → /face-registration → /verify-mfa
//   FIRST_TIME_MFA        → /change-password → /face-registration → /verify-mfa
//   NEW_PASSWORD_REQUIRED → /change-password → /verify-mfa
export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const from = (location.state as { from?: Location })?.from?.pathname || ROUTES.checkin;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: yupResolver(schema) });

  // Failed validation: the per-field messages stay under the inputs, and the same toast the
  // rest of the flow uses announces the failure at the top of the screen (Figma).
  const onInvalid = () => {
    toast.error(messages.auth.loginError, { icon: <Icon name="warning" size={27} /> });
  };

  const onSubmit = async (data: LoginForm) => {
    setSubmitting(true);
    try {
      const res = await login(data.username, data.password);
      switch (res.challenge) {
        // Password not changed yet — both go to change-password; next step differs by challenge.
        case "FIRST_TIME_MFA":
        case "NEW_PASSWORD_REQUIRED":
          navigate(ROUTES.changePassword, { replace: true });
          return;
        // First-time user (face not registered yet): after face registration they also enroll
        // secret questions — unless the backend already has them enrolled.
        case "FACE_REGISTRATION":
          auth.signin(res.user, "unregistered", res.secretRegistered ? "registered" : "unregistered");
          navigate(ROUTES.faceRegistration, { replace: true });
          return;
        // Enrollment was interrupted (lost connection / left the page): face is already
        // registered, so resume straight at the secret-question enrollment page.
        case "SECRET_QUESTION_REQUIRED":
          auth.signin(res.user, "registered", "unregistered");
          navigate(ROUTES.secretQuestions, { replace: true });
          return;
        // Face already registered. Enroll secret questions first when the backend flags them
        // as not yet registered (secretRegistered === false); otherwise onboarding is done →
        // go straight to MFA verify (or the original destination).
        case "FACE_MFA":
        default:
          if (!res.secretRegistered) {
            auth.signin(res.user, "registered", "unregistered");
            navigate(ROUTES.secretQuestions, { replace: true });
            return;
          }
          auth.signin(res.user, "registered", "registered");
          navigate(from, { replace: true });
          return;
      }
    } catch (err: any) {
      const msg = err?.error?.message || err?.message || messages.auth.loginFailed;
      toast.error(msg, { icon: <Icon name="warning" size={27} /> });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={messages.auth.mfaSetupTitle} className="login-shell">
      <form className="auth-form login-form" onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
        <h2 className="login-card-title">{messages.auth.idPasswordAuthTitle}</h2>

        <label className="field">
          <span>{messages.auth.userInfo}</span>
          <input
            autoComplete="username"
            placeholder={messages.auth.userInfoPlaceholder}
            disabled={submitting}
            aria-invalid={errors.username ? "true" : undefined}
            {...register("username")}
          />
          {errors.username ? <p className="field-error">{errors.username.message}</p> : null}
        </label>

        <label className="field">
          <span>{messages.auth.password}</span>
          <div className="field-input-wrap">
            <input
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              placeholder={messages.auth.passwordPlaceholder}
              disabled={submitting}
              aria-invalid={errors.password ? "true" : undefined}
              {...register("password")}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? messages.auth.hidePassword : messages.auth.showPassword}
              aria-pressed={showPassword}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          {errors.password ? <p className="field-error">{errors.password.message}</p> : null}
        </label>


        <button className="primary-button button-full" type="submit" disabled={submitting}>
          {messages.auth.loginButton}
        </button>
      </form>
    </AuthShell>
  );
}
